// ============================================================
// useEchoPrevention - JSON-comparison echo prevention for
// Firestore sync loops.
// ============================================================
// Firestore onSnapshot fires on your own writes. Boolean flags
// fail because the echo arrives asynchronously. Instead, we
// compare the JSON string of the data against a cached version
// and only accept/write when the JSON differs - canonical JSON, keys
// sorted, so it is the VALUES that must differ (v8.393.3).
// ============================================================

import { useRef } from 'react';
import type { MutableRefObject } from 'react';
import { canonicalJson } from '../engine/canonicalJson';

export interface EchoPrevention {
  /** Check if a local change should be written to Firestore. Updates the cache when returning true. */
  shouldWrite(key: string, data: unknown): boolean;
  /** Check if a remote snapshot should be accepted into local state. Updates the cache when returning true. */
  shouldAccept(key: string, data: unknown): boolean;
  /** Manually set the cache for a key (e.g. after a batch write). */
  setCache(key: string, data: unknown): void;
  /** Remove a cached key (e.g. when a target tab is deleted). */
  deleteCache(key: string): void;
  /** Read the raw cached JSON string for a key. */
  getCache(key: string): string;
  /** Forget everything - the cache is only valid for one session. */
  clearCache(): void;
  /** Direct ref access (for advanced consumers). */
  cacheRef: MutableRefObject<Record<string, string>>;
}

type Cache = MutableRefObject<Record<string, string>>;

/**
 * Whether `data` differs from what is cached under `key` - and if it does,
 * cache it. By VALUE (canonicalJson), never by key order: one state built
 * in two places with its keys set in two orders read as a change, and two
 * screens re-wrote it at each other for ever (v8.393.3).
 */
function takeIfChanged(cache: Cache, key: string, data: unknown): boolean {
  const json = canonicalJson(data);
  if (json === (cache.current[key] || '')) return false;
  cache.current[key] = json;
  return true;
}

/**
 * Forget everything.
 *
 * The cache means "this value is already in Firestore", which is only
 * true of ONE session. Carrying it across a session change makes every
 * unchanged document look already-written, so it is never sent to the
 * new session and silently goes missing there.
 */
function clear(cache: Cache): void {
  cache.current = {};
}

/**
 * useEchoPrevention - keyed JSON cache for preventing Firestore
 * echo (write → onSnapshot → re-write) loops.
 *
 * Each key (e.g. a tab ID, or 'atkProfiles') stores the last-known
 * JSON string. `shouldWrite` and `shouldAccept` return false when
 * the data hasn't changed, and automatically update the cache when
 * it has - the same test from either direction.
 */
export default function useEchoPrevention(): EchoPrevention {
  const jsonCacheRef = useRef<Record<string, string>>({});
  return {
    shouldWrite: (key, data) => takeIfChanged(jsonCacheRef, key, data),
    shouldAccept: (key, data) => takeIfChanged(jsonCacheRef, key, data),
    setCache: (key, data) => {
      jsonCacheRef.current[key] = canonicalJson(data);
    },
    deleteCache: (key) => {
      delete jsonCacheRef.current[key];
    },
    getCache: (key) => jsonCacheRef.current[key] || '',
    clearCache: () => clear(jsonCacheRef),
    cacheRef: jsonCacheRef,
  };
}
